import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface FileItem {
  _id: string;
  mimeType: string;
  originalname: string;
  size: number;
  duration?: number;
  url?: string;
  createdAt?: string;
}

interface ImageSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (file: { fileId: string; url: string; video?: boolean }) => void;
  title?: string;
  description?: string;
  acceptVideo?: boolean;
}

export function ImageSelectDialog({
  open,
  onOpenChange,
  onSelect,
  title,
  description,
  acceptVideo = false,
}: ImageSelectDialogProps) {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFileInput, setUploadFileInput] = useState<HTMLInputElement | null>(null);

  const resolvedTitle =
    title ?? (acceptVideo ? "Select Image or Video" : "Select Image");
  const resolvedDescription =
    description ??
    (acceptVideo
      ? "Choose an image or video from your uploaded files or upload a new one"
      : "Choose an image from your uploaded files or upload a new one");

  const fetchFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const mimeTypes = acceptVideo ? ["image", "video"] : ["image"];
      const results = await Promise.all(
        mimeTypes.map(async (mimeType) => {
          const res = await fetch(`/api/files?mimeType=${mimeType}`);
          const data = await res.json();
          return (data.files || []) as FileItem[];
        })
      );
      const nextFiles = results.flat().sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
      setFiles(nextFiles);
    } catch (error) {
      console.error("Failed to fetch files:", error);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFiles();
    }
  }, [open, acceptVideo]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (
      !file.type.startsWith("image/") &&
      !(acceptVideo && file.type.startsWith("video/"))
    ) {
      alert(
        acceptVideo
          ? "Please select an image or video file"
          : "Please select an image file"
      );
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch("/api/files", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to upload file");
      }

      const data = await res.json();

      if (data.file?._id && data.file?.url) {
        onSelect({
          fileId: data.file._id,
          url: data.file.url,
          ...(typeof data.file.mimeType === "string" &&
          data.file.mimeType.startsWith("video/")
            ? { video: true }
            : {}),
        });
        onOpenChange(false);
      }

      await fetchFiles();

      if (uploadFileInput) {
        uploadFileInput.value = "";
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(error instanceof Error ? error.message : "Failed to upload file");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectFile = (file: FileItem) => {
    if (file.url) {
      onSelect({
        fileId: file._id,
        url: file.url,
        ...(file.mimeType.startsWith("video/") ? { video: true } : {}),
      });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{resolvedTitle}</DialogTitle>
          <DialogDescription>{resolvedDescription}</DialogDescription>
        </DialogHeader>
        <div className="mb-4">
          <input
            ref={setUploadFileInput}
            type="file"
            accept={acceptVideo ? "image/*,video/*" : "image/*"}
            onChange={handleFileUpload}
            className="hidden"
            id="image-select-upload"
            disabled={isUploading}
          />
          <label
            htmlFor="image-select-upload"
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                Uploading...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                {acceptVideo ? "Upload Image or Video" : "Upload Image"}
              </>
            )}
          </label>
        </div>
        {isLoadingFiles ? (
          <div className="flex items-center justify-center py-4">
            <div className="text-gray-500">Loading files...</div>
          </div>
        ) : files.length === 0 ? (
          <div className="flex items-center justify-center py-4">
            <div className="text-gray-500">
              {acceptVideo ? "No image or video files found" : "No image files found"}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4 py-4">
            {files.map((file) => (
              <div
                key={file._id}
                className="border rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => handleSelectFile(file)}
              >
                <div className="aspect-video bg-gray-100 flex items-center justify-center overflow-hidden">
                  {file.url && file.mimeType.startsWith("video/") ? (
                    <video
                      src={file.url}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                    />
                  ) : file.url ? (
                    <img
                      src={file.url}
                      alt={file.originalname}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                        const parent = (e.target as HTMLImageElement).parentElement;
                        if (parent) {
                          parent.innerHTML = `<div class="text-gray-400 text-sm p-4 text-center">${file.originalname}</div>`;
                        }
                      }}
                    />
                  ) : (
                    <div className="text-gray-400 text-sm p-4 text-center">
                      {file.originalname}
                    </div>
                  )}
                </div>
                <div className="p-2 border-t">
                  <div className="text-sm font-medium truncate">{file.originalname}</div>
                  <div className="text-xs text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
